import { isEmpty } from 'lodash-es';

import {
  ActivateDeactivateCommands,
  ActiveModeCommands,
  CompanyUser,
  DhwScheduleModes,
  Feature,
  FeatureDeprecated,
  FeatureNames,
  HeatingDhwProperties,
  HeatingRodStatusProperties,
  IsActiveProperty,
  IsEnabledProperty,
  IsNumberValueProperty,
  IsStringValueProperty,
  ScheduleCommands,
  ScheduleProperties,
  SchedulePropertiesValue,
  UserDetails,
  UserPropertyEnum,
} from '@vi-iot/client';
import { LogType } from '@vi-iot/ui';
import { logger } from '@vi-iot/ui/services/logger.service';

import { NO_VALUE } from '@pipes';
import { ScheduleEntry } from '../../features/timemanager/services';
import { IAMRoles } from '../_enums';
import { UpdateFeatureConfig } from '../_models';
import { InstallationFeaturesHelpers } from '../installation-features/installation-features.helpers';
import { InstallationFeaturesState } from '../installation-features/installation-features.state';
import { MapToPayload } from './helpers.model';

export namespace Helpers {
  /**
   *
   * @param payload
   */
  export const createFeatures = (payload: {
    data: FeatureDeprecated[];
  }): { [featureName: string]: FeatureDeprecated<{ isEnabled: boolean; isReady: boolean }> } => {
    const features = {};

    logger.log('--- not ready or not enabled features: ---', LogType.info);
    payload.data.forEach((feature) => {
      // log all notReady or notEnabled features
      if (!feature.isReady || !feature.isEnabled) {
        logger.log(feature.feature, LogType.info);
        logger.log(feature, LogType.info);
      }

      features[feature.feature] = feature;
    });

    return features;
  };

  /**
   *
   * @param payload
   */
  export const extractOperatingModes = (payload: {
    data: Feature<unknown, unknown>[];
  }): {
    circuits: string[];
    mode: { [key: string]: Feature<unknown, unknown> };
    isDhw: boolean;
  } => {
    const operatingModes: {
      circuits: string[];
      mode: { [key: string]: Feature<unknown, unknown> };
      isDhw: boolean;
    } = {
      circuits: [],
      mode: {},
      isDhw: false,
    };

    if (!payload || !payload.data) {
      return operatingModes;
    }

    const featureList: { [key: string]: Feature<unknown, unknown> } = {};
    payload.data.map((feature) => {
      featureList[feature.feature] = feature;
    });
    // const isDhw = isDhwActivated(featureList); // NEED TO BE DONE WHEN MIGRATING FEATURES OPERATING MODE
    const isDhw = { isDhw: false };

    const circuits = payload.data.find(
      (feature) => feature.feature === FeatureNames.circuits
    ) as Feature<IsEnabledProperty, never>;
    const enabled: string[] = circuits ? circuits?.properties?.enabled?.value : [];

    if (enabled.length) {
      enabled.forEach((circuitId) => {
        const operatingMode = payload.data.find(
          (feature) => feature.feature === FeatureNames.circuitActiveMode(circuitId)
        );
        if (operatingMode) {
          operatingModes.circuits.push(circuitId);
          operatingModes.mode[circuitId] = operatingMode;
        }
      });
    }

    return {
      ...operatingModes,
      ...isDhw,
    };
  };

  /**
   *
   * @param action
   */
  export const mapToPayload = (action: {
    feature: Feature<unknown, unknown>;
    featureName: string;
    value: any;
    propertyName: string | string[];
    paramName: string | string[];
    commandName?: string;
    config?: UpdateFeatureConfig;
    deviceId?: string;
  }): MapToPayload => {
    const payload: { [key: string]: any } = {};
    const ghostProperty: { [key: string]: { value: any } } = {};

    if (typeof action.propertyName === 'string') {
      ghostProperty[action.propertyName] = { value: action.value };
    } else if (Array.isArray(action.propertyName)) {
      (action.propertyName as string[]).forEach((property, id) => {
        ghostProperty[property] = { value: action.value[id] };
      });
    }

    const commandName =
      action.commandName && action.feature.commands[action.commandName]
        ? action.commandName
        : Object.keys(action.feature.commands)[0];
    const uri = action?.feature?.commands?.[commandName]?.uri;
    const params = action?.feature?.commands?.[commandName]?.params || {};

    // create payload for given param names
    if (typeof action.paramName === 'string' && action.paramName) {
      payload[action.paramName] = action.value;
    } else if (Array.isArray(action.paramName)) {
      (action.paramName as string[]).forEach((param, i) => {
        if (param) {
          payload[param] = action.value[i];
        }
      });
    }

    // complete payload with other required command params
    Object.keys(params).forEach((param) => {
      if (payload[param] === undefined && params[param].required) {
        const prop = action.feature.properties[param] || ghostProperty[param];
        if (prop) {
          payload[param] = ghostProperty[param]?.value ?? prop.value;
        }
      }
    });

    if (action.config && action.config.excludeParams) {
      action.config.excludeParams.forEach((param) => {
        delete payload[param];
      });
    }

    if (!action.config) {
      action.config = {};
    }

    return {
      ...action,
      payload,
      uri,
    };
  };

  /**
   *
   * @param companyClient
   */
  export const mapCompanyClientUserToUser = (companyClient: CompanyUser): UserDetails => ({
    name: { firstName: companyClient.firstName, familyName: companyClient.familyName },
    // Setting default role value - https://jira.viessmann-climatesolutions.com/browse/VIG-2110
    properties: [{ name: UserPropertyEnum.IdentityType, value: IAMRoles.maintainer }],
    id: companyClient.iamId,
    loginId: companyClient.login,
  });

  /**
   *
   * @param seconds
   * @param currTime
   */
  export const getProperCallbackTimeFormat = (seconds: number, currTime = new Date()): string => {
    let minutes = Math.floor(seconds / 60) < 0 ? 1 : Math.floor(seconds / 60);

    const wholeMinuteCount = currTime.getHours() * 60 + currTime.getMinutes() + minutes;
    let h: any = Math.floor(wholeMinuteCount / 60);
    let m: any = wholeMinuteCount % 60;

    h = h > 23 ? h % 24 : h;

    h = h < 10 ? `0${h}` : h;
    m = m < 10 ? `0${m}` : m;

    return `${h}:${m}`;
  };

  interface CallbackWorkingHoursPayload {
    start: string;
    end: string;
  }

  export const callbackTimeDefaultConfig = {
    workingHoursWeek: { start: '07', end: '17' },
    workingHoursWeekend: { start: '07', end: '16' },
  };

  /**
   *
   * @param callbackTime
   */
  export const isCallbackTimeWithinWorkingHours = (
    callbackTime: string,
    config: {
      workingHoursWeek: CallbackWorkingHoursPayload;
      workingHoursWeekend: CallbackWorkingHoursPayload;
    }
  ): boolean => {
    const currentDay = new Date().getDay();
    const isWeekend = currentDay === 0 || currentDay === 6;
    const callbackHour = callbackTime.substring(0, 2);
    const checkHoursInRange = (
      workingHours: { start: string; end: string },
      hour: string
    ): boolean => +workingHours.start <= +hour && +workingHours.end - 1 >= +hour;

    return isWeekend
      ? checkHoursInRange(config.workingHoursWeekend, callbackHour)
      : checkHoursInRange(config.workingHoursWeek, callbackHour);
  };

  export function isDhwModeInAnyCircuitOrDhwOn(
    installationsFeaturesState: InstallationFeaturesState,
    featuresBaseUri: string,
    selectedInstallationId: number,
    selectedGatewaySerial: string,
    selectedDeviceId: string
  ): boolean {
    const circuitsFeatureUri = InstallationFeaturesHelpers.constructInstallationFeatureUri(
      featuresBaseUri,
      FeatureNames.circuits,
      selectedGatewaySerial,
      selectedInstallationId,
      selectedDeviceId
    );
    const circuitsFeature = installationsFeaturesState.entities[circuitsFeatureUri] as Feature<
      IsEnabledProperty,
      never
    >;

    const circuitIds: string[] = circuitsFeature?.properties?.enabled?.value || [];

    return circuitIds.some((circuitId) => {
      const circuitActiveModeUri = InstallationFeaturesHelpers.constructInstallationFeatureUri(
        featuresBaseUri,
        FeatureNames.circuitActiveMode(circuitId),
        selectedGatewaySerial,
        selectedInstallationId,
        selectedDeviceId
      );
      const activeMode = installationsFeaturesState.entities[circuitActiveModeUri] as Feature<
        IsStringValueProperty,
        ActiveModeCommands
      >;
      const dhwUri = InstallationFeaturesHelpers.constructInstallationFeatureUri(
        featuresBaseUri,
        FeatureNames.dhw,
        selectedGatewaySerial,
        selectedInstallationId,
        selectedDeviceId
      );
      const dhwMode = installationsFeaturesState.entities[dhwUri] as Feature<
        HeatingDhwProperties,
        never
      >;

      const activeModeValue: string = activeMode?.properties?.value?.value;
      const isCircuitInDhwMode = Object.values(DhwScheduleModes).includes(
        activeModeValue as DhwScheduleModes
      );

      const dhwOneTimeChargeUri = InstallationFeaturesHelpers.constructInstallationFeatureUri(
        featuresBaseUri,
        FeatureNames.dhwOneTimeCharge,
        selectedGatewaySerial,
        selectedInstallationId,
        selectedDeviceId
      );
      const dhwOneTimeCharge = installationsFeaturesState.entities[dhwOneTimeChargeUri] as Feature<
        IsActiveProperty,
        ActivateDeactivateCommands
      >;
      const isDhwOneTimeChargeActive: boolean = dhwOneTimeCharge?.properties?.active?.value;
      const isDhwActiveAndOn: boolean =
        dhwMode?.properties?.active?.value && dhwMode?.properties?.status?.value === 'on';

      return isCircuitInDhwMode || isDhwOneTimeChargeActive || isDhwActiveAndOn;
    });
  }

  export function _isDhwScheduleModeOn(
    schedule: SchedulePropertiesValue,
    offsetTime: number
  ): boolean {
    const currentDate = offsetTime
      ? new Date(new Date().getTime() + offsetTime * 60 * 1000)
      : new Date();
    const weekday = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

    const currentDaySchedule = schedule[weekday[currentDate.getUTCDay()]];

    return currentDaySchedule.some((scheduleEntry: ScheduleEntry) => {
      if (!scheduleEntry.start || !scheduleEntry.end || !scheduleEntry.mode) {
        return false;
      }

      const startHours = +scheduleEntry.start.split(':')[0];
      const startMinutes = +scheduleEntry.start.split(':')[1];
      const endHours = +scheduleEntry.end.split(':')[0];
      const endMinutes = +scheduleEntry.end.split(':')[1];

      if (isNaN(startHours) || isNaN(startMinutes) || isNaN(endHours) || isNaN(endMinutes)) {
        return false;
      }

      const currentHours = currentDate.getUTCHours();
      const currentMinutes = currentDate.getUTCMinutes();

      const isCurrentDateAfterStartDate =
        currentHours > startHours || (currentHours >= startHours && currentMinutes >= startMinutes);
      const isCurrentDateBeforeEndDate =
        currentHours < endHours || (currentHours <= endHours && currentMinutes <= endMinutes);

      return isCurrentDateAfterStartDate && isCurrentDateBeforeEndDate;
    });
  }

  export function _isDhwAvailable(
    installationsFeaturesState: InstallationFeaturesState,
    featuresBaseUri: string,
    selectedInstallationId: number,
    selectedGatewaySerial: string,
    selectedDeviceId: string
  ): boolean {
    const dhwOneTimeChargeUri = InstallationFeaturesHelpers.constructInstallationFeatureUri(
      featuresBaseUri,
      FeatureNames.dhwOneTimeCharge,
      selectedGatewaySerial,
      selectedInstallationId,
      selectedDeviceId
    );
    const dhwOneTimeChargeFeature = installationsFeaturesState.entities[
      dhwOneTimeChargeUri
    ] as Feature<IsActiveProperty, never>;

    const dhwScheduleUri = InstallationFeaturesHelpers.constructInstallationFeatureUri(
      featuresBaseUri,
      FeatureNames.dhwSchedule,
      selectedGatewaySerial,
      selectedInstallationId,
      selectedDeviceId
    );
    const dhwScheduleFeature = installationsFeaturesState.entities[dhwScheduleUri] as Feature<
      ScheduleProperties,
      ScheduleCommands
    >;

    const heatingDeviceTimeOffsetUri = InstallationFeaturesHelpers.constructInstallationFeatureUri(
      featuresBaseUri,
      FeatureNames.heatingDeviceTimeOffset,
      selectedGatewaySerial,
      selectedInstallationId,
      selectedDeviceId
    );
    const heatingDeviceTimeOffsetFeature = installationsFeaturesState.entities[
      heatingDeviceTimeOffsetUri
    ] as Feature<IsNumberValueProperty, never>;

    const oneTimeChargeActive = dhwOneTimeChargeFeature?.properties?.active?.value || null;
    const dhwScheduleActive = dhwScheduleFeature?.properties?.active?.value || null;
    const offsetTime = heatingDeviceTimeOffsetFeature?.properties?.value?.value || null;

    if (oneTimeChargeActive) {
      return true;
    } else if (dhwScheduleActive) {
      const schedule = dhwScheduleFeature?.properties?.entries?.value || null;

      return schedule ? _isDhwScheduleModeOn(schedule, offsetTime) : false;
    } else {
      return false;
    }
  }

  export function _isDhwAvailableForMultiFamilyHouse(
    installationsFeaturesState: InstallationFeaturesState,
    featuresBaseUri: string,
    selectedInstallationId: number,
    selectedGatewaySerial: string,
    selectedDeviceId: string
  ): boolean {
    const circuitsFeatureUri = InstallationFeaturesHelpers.constructInstallationFeatureUri(
      featuresBaseUri,
      FeatureNames.circuits,
      selectedGatewaySerial,
      selectedInstallationId,
      selectedDeviceId
    );
    const circuitsFeature = installationsFeaturesState.entities[circuitsFeatureUri] as Feature<
      IsEnabledProperty,
      never
    >;
    const heatingDeviceTimeOffsetFeatureUri = InstallationFeaturesHelpers.constructInstallationFeatureUri(
      featuresBaseUri,
      FeatureNames.heatingDeviceTimeOffset,
      selectedGatewaySerial,
      selectedInstallationId,
      selectedDeviceId
    );
    const offsetTimeFeature = installationsFeaturesState.entities[
      heatingDeviceTimeOffsetFeatureUri
    ] as Feature<IsNumberValueProperty, never>;

    const circuitIds = circuitsFeature?.properties?.enabled?.value || [];
    const offsetTime = offsetTimeFeature?.properties?.value?.value || null;

    let circuit = null;

    circuitIds.forEach((id) => {
      const circuitDhwScheduleUri = InstallationFeaturesHelpers.constructInstallationFeatureUri(
        featuresBaseUri,
        FeatureNames.circuitDhwSchedule(id),
        selectedGatewaySerial,
        selectedInstallationId,
        selectedDeviceId
      );
      const feature = installationsFeaturesState.entities[circuitDhwScheduleUri] as Feature<
        ScheduleProperties,
        ScheduleCommands
      >;

      const isFeatureActive = feature?.properties?.active?.value || null;

      if (isFeatureActive) {
        circuit = feature;
      }
    });

    if (circuit) {
      const schedule = circuit?.properties?.entries?.value || null;

      return schedule ? _isDhwScheduleModeOn(schedule, offsetTime) : false;
    } else {
      return false;
    }
  }

  export function isDhwActivated(
    installationsFeaturesState: InstallationFeaturesState,
    featuresBaseUri: string,
    selectedInstallationId: number,
    selectedGatewaySerial: string,
    selectedDeviceId: string
  ): { isDhw: boolean } {
    const configurationMultiFamilyHouseUri = InstallationFeaturesHelpers.constructInstallationFeatureUri(
      featuresBaseUri,
      FeatureNames.configurationMultiFamilyHouse,
      selectedGatewaySerial,
      selectedInstallationId,
      selectedDeviceId
    );
    if (!Object.keys(installationsFeaturesState.entities)?.length) {
      return { isDhw: false };
    }
    const feature = installationsFeaturesState.entities[
      configurationMultiFamilyHouseUri
    ] as Feature<IsActiveProperty, never>;

    if (
      !isDhwModeInAnyCircuitOrDhwOn(
        installationsFeaturesState,
        featuresBaseUri,
        selectedInstallationId,
        selectedGatewaySerial,
        selectedDeviceId
      )
    ) {
      return { isDhw: false };
    }

    const isMultiFamilyHouseFeature = feature?.properties?.active?.value || null;

    return isMultiFamilyHouseFeature
      ? {
          isDhw: _isDhwAvailableForMultiFamilyHouse(
            installationsFeaturesState,
            featuresBaseUri,
            selectedInstallationId,
            selectedGatewaySerial,
            selectedDeviceId
          ),
        }
      : {
          isDhw: _isDhwAvailable(
            installationsFeaturesState,
            featuresBaseUri,
            selectedInstallationId,
            selectedGatewaySerial,
            selectedDeviceId
          ),
        };
  }

  export function getHeatingRodStatusValue(
    feature: Feature<HeatingRodStatusProperties, never>
  ): string {
    if (!feature || isEmpty(feature.properties)) {
      return NO_VALUE;
    }

    const props = feature.properties;

    if (props.level3?.value) {
      return '3';
    } else if (props.level2?.value) {
      return '2';
    } else if (props.level1?.value) {
      return '1';
    } else {
      return '+INSTALLATIONS.INSTALLATION.ELECTRIC_HEATER.STATUS.OFF';
    }
  }
}
